import React from 'react';
import { Link } from 'react-router-dom';
import classnames from 'classnames';

import styles from './userAccountLeftSideBarStyles.scss';

import Button, { BUTTON_TYPES } from '@buyside/components/presentation/Button/button';
import ChevronBlue from '@buyside/svgs/chevron_blue.svg';

import { profileUrl } from '@buyside/util/urlHelper';

import LoadingSpinner, {
  SIZES
} from '@buyside/components/presentation/LoadingSpinner/loadingSpinner';

import { useCurrentUserAccountLeftSideBarQuery } from '@interfaces/graphql-typings';

import { User } from '@interfaces/graphql-typings';

export const watchlistPages = [
  { page: '/search_alerts', label: 'Saved Searches' },
  { page: '/watchlist', label: 'Watchlisted Tickers' },
  { page: '/reading_list', label: 'Reading List' },
  { page: '/archived_reading_list', label: 'Archived Reading List' },
  { page: '/following', label: 'Users You Follow' },
  { page: '/followers', label: 'Users Following You' }
];

const careerItemsPages = ({
  canWriteCompensations,
  canPostJobs,
  canApplyToJobs
}: Pick<User, 'canApplyToJobs' | 'canPostJobs'> & { canWriteCompensations: boolean }): {
  page: string;
  label: string;
}[] => {
  return [
    canWriteCompensations ? { page: '/compensations', label: 'Posted Compensations' } : null,
    canPostJobs ? { page: '/job_postings', label: 'Jobs Posted' } : null,
    canApplyToJobs ? { page: '/job_applications', label: 'Jobs Applied To' } : null
  ].filter(Boolean) as { page: string; label: string }[];
};

export const accountPages = [
  { page: '/', label: 'Change Email & Password' },
  { page: '/notifications', label: 'Email Notifications' },
  { page: '/site_notifications', label: 'Site Notifications' },
  { page: '/billing_information', label: 'Billing Information' },
  { page: '/viewer_intro_requests', label: 'Received Intro Requests' }
];

const mapSideBarItems = ({
  page,
  label,
  pathname
}: {
  page: string;
  label: string;
  pathname?: string;
}): React.ReactElement => {
  return (
    <div className={styles.sidebarLink} key={page}>
      {pathname === page && <ChevronBlue className={styles.pageArrow} />}
      <Link to={page}>
        <span className={classnames({ [styles.linkText]: pathname === page })}>{label}</span>
      </Link>
    </div>
  );
};

const UserAccountLeftSideBar: React.FC<{
  pathname: string;
}> = ({ pathname }) => {
  const { loading, data } = useCurrentUserAccountLeftSideBarQuery();

  if (loading || !data?.currentUser) {
    return (
      <div className={styles.sidebarContainer}>
        <LoadingSpinner size={SIZES.LARGE} />
      </div>
    );
  }

  const {
    profile,
    hasBadges,
    hasIdeas,
    canPostJobs,
    canApplyToJobs,
    canWriteCompensations,
    isTopDown
  } = data.currentUser;

  if (!profile) return null;

  return (
    <div className={styles.sidebarContainer}>
      <div>
        <Button
          type={BUTTON_TYPES.MAJOR}
          url={profileUrl(profile.id)}
          text="View/Edit Your Profile"
        />
      </div>
      <div>
        <h5 className={styles.sidebarHeader}>Your Research</h5>
        {hasIdeas && mapSideBarItems({ page: '/ideas', label: 'Ideas', pathname })}
        {mapSideBarItems({ page: '/ratings', label: 'Your Ratings', pathname })}
        {hasBadges && mapSideBarItems({ page: '/badges', label: 'Your Badges', pathname })}
      </div>
      <div>
        <h5 className={styles.sidebarHeader}>Your Watchlist</h5>
        {watchlistPages.map(item => mapSideBarItems({ ...item, pathname }))}
      </div>
      {(canPostJobs || canApplyToJobs || canWriteCompensations) && (
        <div>
          <h5 className={styles.sidebarHeader}>Your Career Items</h5>
          {careerItemsPages({
            canWriteCompensations: Boolean(canWriteCompensations),
            canPostJobs,
            canApplyToJobs
          }).map(item => mapSideBarItems({ ...item, pathname }))}
        </div>
      )}
      <h5 className={styles.sidebarHeader}>Your Account</h5>
      {!isTopDown && mapSideBarItems({ page: '/referrals', label: 'Your Referrals', pathname })}
      {accountPages.map(item => mapSideBarItems({ ...item, pathname }))}
    </div>
  );
};

export default UserAccountLeftSideBar;
